import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { RecorderModal } from './RecorderModal';
import { RecordType } from '../types';
import { uploadFileToMoodle } from '../../../core/api/fileApi';
import { toast } from 'react-toastify';
import { RECORDER_DRAFT_ITEM_ID } from '../constants';

interface RecorderState {
  isOpen: boolean;
  recordType: RecordType;
  editorId: string;
}

export const TinyMCERecorderWrapper: React.FC = () => {
  const [recorderState, setRecorderState] = useState<RecorderState>({
    isOpen: false,
    recordType: 'audio',
    editorId: '',
  });
  
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Sử dụng itemid cố định duy nhất
  const itemIdRef = useRef<number>(RECORDER_DRAFT_ITEM_ID);

  useEffect(() => {
    const handleOpenRecorder = (event: any) => {
      const { type, editorId } = event.detail;
      setRecorderState({
        isOpen: true,
        recordType: type,
        editorId: editorId,
      });
      setErrorMessage(null);
    };

    window.addEventListener('tinymce-open-recorder', handleOpenRecorder);

    return () => {
      window.removeEventListener('tinymce-open-recorder', handleOpenRecorder);
    };
  }, []);

  const handleClose = useCallback(() => {
    setRecorderState((prev) => ({ ...prev, isOpen: false }));
    setErrorMessage(null);
  }, []);

  const handleSave = useCallback(
    async (blob: Blob, type: RecordType) => {
      try {
        setIsUploading(true);
        setErrorMessage(null);

        const editorMaxSize = (window as any).editorMaxFileSize;
        if (editorMaxSize && blob.size > editorMaxSize) {
          const maxSizeMessage = (window as any).editorMaxFileSizeMessage;
          setErrorMessage(
            maxSizeMessage?.detail ||
              "File size exceeds the allowed limit."
          );
          setIsUploading(false);
          return;
        }

        // Tạo tên file với chuỗi số ngẫu nhiên (tránh tăng dần)
        const extension = type === 'video' ? 'webm' : 'webm';
        const randomSuffix = `${Date.now()}${Math.floor(
          Math.random() * 1000000
        )}`;
        const filename = `${type}_${randomSuffix}.${extension}`;
        
        // Tạo File object từ Blob với tên file cụ thể
        const fileObject = new File([blob], filename, { 
          type: blob.type || (type === 'video' ? 'video/webm' : 'audio/webm')
        });
        
        // Lấy contextid từ window object (được set từ CustomEditor)
        const contextid = (window as any).editorContextId || 1;
        
        // Chuẩn bị tham số để upload lên Moodle
        const uploadParams = {
          contextid: contextid, // Context ID từ assignment
          component: 'user', // Component - có thể là 'user', 'mod_assign', etc.
          filearea: 'draft', // File area - 'draft' cho file tạm thời
          itemid: itemIdRef.current, // Sử dụng itemid chung cho tất cả files
          filepath: '/', // Đường dẫn file trong Moodle
          filename: filename,
          file: fileObject, // Sử dụng File object thay vì blob
        };
        
        // Upload file lên Moodle
        const uploadResponse = await uploadFileToMoodle(uploadParams);
        
        if (uploadResponse && uploadResponse[0]) {
          const uploadedFile = uploadResponse[0];
          
          // Tạo URL từ file đã upload (nếu có)
          const fileUrl = uploadedFile.url || URL.createObjectURL(blob);
          
          // Tạo HTML để insert vào editor với data attributes để lưu thông tin file
          // TinyMCE sẽ tự động wrap với span.mce-preview-object và thêm prefix data-mce-p-
          let html = '';
          const dataAttributes = `
            data-moodle-file="true" 
            data-contextid="${uploadedFile.contextid}" 
            data-component="${uploadedFile.component}" 
            data-filearea="${uploadedFile.filearea}" 
            data-itemid="${uploadedFile.itemid}" 
            data-filepath="${uploadedFile.filepath}" 
            data-filename="${uploadedFile.filename}"
          `.trim();
          
          if (type === 'video') {
            html = `<p><video controls src="${fileUrl}" ${dataAttributes} style="max-width: 100%; height: auto;"></video></p>`;
          } else {
            html = `<p><audio controls src="${fileUrl}" ${dataAttributes}></audio></p>`;
          }

          // Dispatch event để TinyMCE plugin xử lý
          const event = new CustomEvent('tinymce-insert-media', {
            detail: {
              type: type,
              editorId: recorderState.editorId,
              html: html,
              blob: blob,
              uploadedFile: uploadedFile,
            },
          });
          window.dispatchEvent(event);
          
          toast.success(`${type === 'video' ? 'Video' : 'Audio'} đã được lưu thành công!`);
          handleClose();
        } else {
          throw new Error('Upload thất bại');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setErrorMessage('Có lỗi xảy ra khi lưu file. Vui lòng thử lại!');
        
        // Fallback: Vẫn insert với blob URL nếu upload thất bại (không có data attributes vì không upload lên Moodle)
      const url = URL.createObjectURL(blob);
      let html = '';
      if (type === 'video') {
        html = `<p><video controls src="${url}" style="max-width: 100%; height: auto;"></video></p>`;
      } else {
        html = `<p><audio controls src="${url}"></audio></p>`;
      }

      const event = new CustomEvent('tinymce-insert-media', {
        detail: {
          type: type,
          editorId: recorderState.editorId,
          html: html,
          blob: blob,
        },
      });
      window.dispatchEvent(event);

      handleClose();
      } finally {
        setIsUploading(false);
      }
    },
    [recorderState.editorId, handleClose]
  );

  // Chỉ render khi modal mở, sử dụng Portal để tránh ghi đè styles
  if (!recorderState.isOpen) {
    return null;
  }

  return createPortal(
    <RecorderModal
      isOpen={recorderState.isOpen}
      onClose={handleClose}
      onSave={handleSave}
      recordType={recorderState.recordType}
      isUploading={isUploading}
      errorMessage={errorMessage}
      onResetError={() => setErrorMessage(null)}
    />,
    document.body
  );
};

